import { Dialog } from '@base-ui/react/dialog'
import type { FormEvent, ReactNode, RefObject } from 'react'
import { Button } from './button'

/**
 * A short form to confirm an action with (a WAI-ARIA dialog, not an alertdialog: what it asks
 * for is its fields): its title, a sentence on what will happen, the fields and two buttons. The
 * focus starts on its first field (`initialFocus`), and Enter in a field sends it, as the
 * confirm button does; Escape closes it, a click outside does not. Only the sentence describes
 * the dialog: each field is read out with its own label, hint and error. While the action runs,
 * the confirm button says so and waits, and the dialog stays open; a refusal shows above the
 * buttons.
 */
export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  submitLabel,
  busyLabel,
  cancelLabel,
  busy = false,
  error,
  onSubmit,
  initialFocus,
  opener,
  fallbackFocus,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** What will happen, under the title. */
  description: ReactNode
  /** The fields. */
  children: ReactNode
  submitLabel: string
  busyLabel?: string
  cancelLabel: string
  busy?: boolean
  /** Why the action was refused, when it is not about a field; already translated. */
  error?: string | null
  onSubmit: () => void
  /** The first field, where the focus starts. */
  initialFocus: RefObject<HTMLElement | null>
  /** Where the focus goes back once it closes, if it is still on the page. */
  opener?: RefObject<HTMLElement | null>
  /**
   * Where the focus goes instead when the opener is gone (the action done removed it), so that
   * it never falls to the page's body: what says the action is done.
   */
  fallbackFocus?: RefObject<HTMLElement | null>
}) {
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!busy) onSubmit()
  }
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next)
      }}
      disablePointerDismissal
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-[70] bg-black/40" />
        <Dialog.Viewport className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-4 sm:items-center">
          <Dialog.Popup
            initialFocus={initialFocus}
            finalFocus={() =>
              opener?.current?.isConnected
                ? opener.current
                : fallbackFocus?.current?.isConnected
                  ? fallbackFocus.current
                  : true
            }
            className="w-full max-w-lg rounded-card bg-surface p-6 shadow-xl outline-none"
          >
            <Dialog.Title className="text-xl [overflow-wrap:anywhere]">{title}</Dialog.Title>
            <Dialog.Description className="mt-3 text-sm">{description}</Dialog.Description>
            <form noValidate onSubmit={submit} className="mt-4 text-sm">
              <div className="space-y-4">{children}</div>
              <p role="alert" className="text-sm text-danger not-empty:mt-4">
                {error ?? ''}
              </p>
              <div className="mt-6 flex flex-wrap justify-end gap-3">
                <Dialog.Close render={<Button variant="secondary">{cancelLabel}</Button>} />
                <Button
                  type="submit"
                  // While it runs the button says so and waits, keeping the focus.
                  aria-disabled={busy || undefined}
                  className="aria-disabled:cursor-progress aria-disabled:opacity-50"
                >
                  {busy && busyLabel ? busyLabel : submitLabel}
                </Button>
              </div>
            </form>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
